// Tela de Campo na Visão Geral (#6505): o motoboy. Pensada para o celular; abre no dia.
//
// Pergunta da tela: qual é a minha rota de hoje?
//   - rota de hoje: paradas, feitas e a próxima, com os botões do Google Maps (mesma regra
//     de navegação das Rotas: coordenada só quando é do endereço);
//   - empresas sem visita: as que entraram em rota dele e nunca foram visitadas, com a
//     opção de ordenar pela mais perto de onde ele está;
//   - resultado da semana (#6507): visitas feitas, com o número do medidor e quantas
//     negociações passaram da Qualificação, nesta semana e nas três anteriores.
import React, { useMemo, useState } from 'react';
import { CheckCircle2, Circle, ExternalLink, LocateFixed, Navigation, Route } from 'lucide-react';
import { googleMapsDirLink } from '../../../src/lib/routeOptimizer';
import { isoLocal } from '../../../src/lib/visaoGeral/periodo';
import { empresasSemVisita, maisPertoPrimeiro, rotaDeHoje, type NegociacaoDaParada } from '../../../src/lib/visaoGeral/campo';
import { resultadoPorSemana } from '../../../src/lib/visaoGeral/comercialFase2';
import { enderecoParada, urlNavegacao } from '../../CRM/Rotas/rotaTypes';
import { BlocosDoDia } from '../BlocosDoDia';
import { CabecalhoVisaoGeral, CartaoHoje, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural } from '../componentes';
import { useRotasDoMotoboy } from '../useCampo';
import type { PropsTelaVisaoGeral } from '../tipos';

const LISTA = 10;
const dataCurta = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');
const ROTULO_STATUS: Record<string, string> = { em_execucao: 'em execução', planejada: 'planejada', agendada: 'agendada', reagendada: 'reagendada' };
const kwh = (n: NegociacaoDaParada) => (Number(n.consumo_medio_kwh) > 0 ? `${Math.round(Number(n.consumo_medio_kwh)).toLocaleString('pt-BR')} kWh` : null);
const endereco = (n: NegociacaoDaParada) => enderecoParada(n as any) || 'Sem endereço';
const navegar = (n: NegociacaoDaParada) => urlNavegacao(n as any);

const BotaoMapa: React.FC<{ href: string; children: React.ReactNode; destaque?: boolean }> = ({ href, children, destaque }) => (
  href ? (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className={`flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold ${destaque ? 'bg-primary text-white hover:bg-primary/90' : 'bg-primary/10 text-primary hover:bg-primary/20'}`}>
      {children}
    </a>
  ) : null
);

const TelaCampo: React.FC<PropsTelaVisaoGeral> = ({ pessoa, definicao, periodo }) => {
  const hoje = isoLocal(new Date());
  const { rotas, erro } = useRotasDoMotoboy(pessoa.id);
  const [posicao, setPosicao] = useState<{ lat: number; lng: number } | null>(null);
  const [localizando, setLocalizando] = useState<'idle' | 'buscando' | 'negado'>('idle');

  const dia = useMemo(() => (rotas ? rotaDeHoje(rotas, hoje) : null), [rotas, hoje]);
  const semVisita = useMemo(() => (rotas ? empresasSemVisita(rotas, hoje) : null), [rotas, hoje]);
  // Visita feita em rota cancelada também conta.
  const semanas = useMemo(() => (rotas ? resultadoPorSemana(rotas.flatMap(r => r.rota_paradas
    .filter(p => p.status === 'visitado' && p.visitado_em)
    .map(p => ({
      negociacao_id: p.negociacao_id,
      visitado_em: p.visitado_em!,
      numero_medidor: p.numero_medidor ?? null,
      status_negociacao: p.negociacoes?.status ?? null,
      stage_negociacao: p.negociacoes?.stage ?? null,
    }))), hoje, 4) : null), [rotas, hoje]);
  const semana = semanas?.[0] ?? null;
  const ultimaVisita = useMemo(() => {
    const datas = (rotas ?? []).flatMap(r => r.rota_paradas).map(p => p.visitado_em).filter((v): v is string => !!v).sort();
    return datas.length ? isoLocal(new Date(datas[datas.length - 1])) : null;
  }, [rotas]);
  const semanasVazias = !!semanas && semanas.every(s => s.visitas === 0);
  const lista = useMemo(() => (semVisita && posicao ? maisPertoPrimeiro(semVisita, posicao) : semVisita?.map(e => ({ ...e, km: null as number | null })) ?? null), [semVisita, posicao]);

  const rotaCompleta = useMemo(() => {
    if (!dia) return '';
    const pontos = dia.pendentes
      .map(p => p.negociacoes)
      .filter((n): n is NegociacaoDaParada => !!n && n.latitude != null && n.longitude != null)
      .map(n => ({ lat: Number(n.latitude), lng: Number(n.longitude) }));
    return pontos.length ? googleMapsDirLink(pontos) : '';
  }, [dia]);

  const localizar = () => {
    if (!navigator.geolocation) { setLocalizando('negado'); return; }
    setLocalizando('buscando');
    navigator.geolocation.getCurrentPosition(
      p => { setPosicao({ lat: p.coords.latitude, lng: p.coords.longitude }); setLocalizando('idle'); },
      () => setLocalizando('negado'),
      { timeout: 8000 },
    );
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} />

      <BlocosDoDia pessoa={pessoa} perfil="campo" />

      <SecaoVisaoGeral titulo="Hoje" subtitulo="minha rota" colunas={3}>
        <CartaoHoje valor={dia ? dia.paradas : null} rotulo="paradas hoje" tom="neutro" para="/crm/rotas" />
        <CartaoHoje valor={dia ? dia.feitas : null} rotulo="visitadas" tom="ok" para="/crm/rotas" />
        <CartaoHoje valor={dia ? dia.pendentes.length : null} rotulo="faltam" tom={dia && dia.pendentes.length ? 'atencao' : 'neutro'} para="/crm/rotas" />
      </SecaoVisaoGeral>
      {erro && <p className="-mt-3 text-xs text-red-600 dark:text-red-400">Não foi possível carregar as suas rotas.</p>}

      <PainelDetalhe titulo={dia?.rota ? `Rota de hoje${dia.rota.nome ? `: ${dia.rota.nome}` : ''}` : 'Rota de hoje'} acao={{ rotulo: 'Minhas rotas', para: '/crm/rotas' }}>
        {!dia ? (
          <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
        ) : !dia.rota ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-600 dark:text-gray-300">Nenhuma rota para hoje.</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {dia.proximaRota
                ? `Próxima rota: ${dataCurta(dia.proximaRota.data)}, com ${plural(dia.proximaRota.rota_paradas.length, 'parada', 'paradas')}.`
                : 'Nenhuma rota marcada para os próximos dias. Fale com a gestão comercial.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {dia.proxima?.negociacoes ? (
              <div className="rounded-xl border border-primary/20 bg-primary/5 p-3 space-y-2">
                <span className="block text-xs font-semibold uppercase tracking-wide text-primary">Próxima parada (nº {dia.proxima.posicao})</span>
                <span className="block text-base font-semibold text-gray-900 dark:text-white">{dia.proxima.negociacoes.titulo || 'Sem nome'}</span>
                <span className="block text-sm text-gray-500 dark:text-gray-400">{endereco(dia.proxima.negociacoes)}{kwh(dia.proxima.negociacoes) ? ` · ${kwh(dia.proxima.negociacoes)}` : ''}</span>
                <BotaoMapa href={navegar(dia.proxima.negociacoes)} destaque><Navigation size={16} /> Navegar até a próxima</BotaoMapa>
              </div>
            ) : (
              <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">Todas as paradas de hoje foram visitadas.</p>
            )}
            <BotaoMapa href={rotaCompleta}><ExternalLink size={16} /> Rota que falta no Google Maps</BotaoMapa>
            <ol className="divide-y divide-gray-100 dark:divide-gray-700">
              {[...dia.rota.rota_paradas].sort((a, b) => a.posicao - b.posicao).map(p => (
                <li key={p.id} className="flex items-start gap-3 py-2.5">
                  {p.status === 'visitado'
                    ? <CheckCircle2 size={18} className="shrink-0 text-emerald-600 mt-0.5" aria-label="Visitada" />
                    : <Circle size={18} className="shrink-0 text-gray-300 mt-0.5" aria-label="Falta visitar" />}
                  <span className="flex-1 min-w-0">
                    <span className={`block text-sm font-medium truncate ${p.status === 'visitado' ? 'text-gray-400 line-through' : 'text-gray-800 dark:text-gray-100'}`}>{p.posicao}. {p.negociacoes?.titulo || 'Sem nome'}</span>
                    <span className="block text-xs text-gray-400 truncate">{p.negociacoes ? endereco(p.negociacoes) : '—'}</span>
                  </span>
                  {p.status !== 'visitado' && p.negociacoes && navegar(p.negociacoes) && (
                    <a href={navegar(p.negociacoes)} target="_blank" rel="noopener noreferrer" aria-label="Navegar até esta parada"
                      className="shrink-0 p-2 rounded-lg text-primary hover:bg-primary/10"><Navigation size={16} /></a>
                  )}
                </li>
              ))}
            </ol>
            <p className="text-xs text-gray-400">A visita (foto do medidor e o que aconteceu) é registrada em Minhas rotas.</p>
          </div>
        )}
        {dia && dia.abertasAntigas.length > 0 && (
          <p className="text-xs text-amber-700 dark:text-amber-400 mt-3">
            {plural(dia.abertasAntigas.length, 'rota de dia que já passou ficou aberta', 'rotas de dias que já passaram ficaram abertas')}:{' '}
            {dia.abertasAntigas.map(r => `${dataCurta(r.data)} (${ROTULO_STATUS[r.status] ?? r.status})`).join(', ')}. Conclua ou peça para remarcar.
          </p>
        )}
      </PainelDetalhe>

      <PainelDetalhe titulo="Empresas que ficaram sem visita" acao={{ rotulo: 'Minhas rotas', para: '/crm/rotas' }}>
        {!lista ? (
          <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
        ) : lista.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Todas as empresas das suas rotas foram visitadas.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                <strong className="text-gray-900 dark:text-white">{lista.length.toLocaleString('pt-BR')}</strong> empresas entraram nas suas rotas e nunca foram visitadas.
              </p>
              <button type="button" onClick={localizar} disabled={localizando === 'buscando'}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 text-primary text-xs font-semibold px-3 py-2 hover:bg-primary/20 disabled:opacity-50">
                <LocateFixed size={14} /> {posicao ? 'Atualizar minha posição' : localizando === 'buscando' ? 'Localizando…' : 'Mais perto de mim'}
              </button>
            </div>
            {localizando === 'negado' && <p className="text-xs text-amber-700 dark:text-amber-400 mb-2">Não foi possível pegar a sua localização. Libere a localização do navegador e tente de novo.</p>}
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {lista.slice(0, LISTA).map(e => (
                <li key={e.negociacao.id} className="flex items-start gap-3 py-2.5">
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{e.negociacao.titulo || 'Sem nome'}</span>
                    <span className="block text-xs text-gray-400 truncate">
                      {endereco(e.negociacao)}{kwh(e.negociacao) ? ` · ${kwh(e.negociacao)}` : ''}
                    </span>
                    <span className="block text-xs text-gray-400">
                      {e.km != null ? `${e.km.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km daqui · ` : ''}na rota de {dataCurta(e.ultimaRota)}{e.vezes > 1 ? ` (${e.vezes} rotas)` : ''}
                    </span>
                  </span>
                  {navegar(e.negociacao) && (
                    <a href={navegar(e.negociacao)} target="_blank" rel="noopener noreferrer" aria-label="Navegar até a empresa"
                      className="shrink-0 p-2 rounded-lg text-primary hover:bg-primary/10"><Navigation size={16} /></a>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-400 mt-3">
              {posicao ? 'Da mais perto para a mais longe de onde você está.' : 'Maior consumo primeiro.'}
              {lista.length > LISTA ? ` Mais ${plural(lista.length - LISTA, 'empresa', 'empresas')}.` : ''}
              {' '}A lista nova de prospecção (por bairro, para os próximos dias) entra aqui quando virar rota.
            </p>
          </>
        )}
      </PainelDetalhe>

      <SecaoVisaoGeral titulo="Minha semana" subtitulo={semana ? `desde segunda, ${dataCurta(semana.semana)}` : undefined} colunas={3}>
        <CartaoHoje valor={semana ? semana.visitas : null} rotulo="visitas feitas" tom="neutro" para="/crm/rotas" />
        <CartaoHoje valor={semana ? semana.comMedidor : null} rotulo="com o número do medidor" tom="ok" para="/crm/rotas" />
        <CartaoHoje valor={semana ? semana.avancaram : null} rotulo="viraram negociação" tom="ok" para="/crm"
          dica="A negociação da empresa visitada passou da Qualificação: teve simulação ou já está em negociação, termo ou pós-venda." />
      </SecaoVisaoGeral>
      {semanas && (
        <PainelDetalhe titulo="Últimas semanas">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 dark:text-gray-400">
                <th className="py-1.5 text-left font-semibold">Semana</th>
                <th className="py-1.5 text-right font-semibold">Visitas</th>
                <th className="py-1.5 text-right font-semibold">Medidor</th>
                <th className="py-1.5 text-right font-semibold">Viraram negociação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {semanas.map((s, i) => (
                <tr key={s.semana} className={i === 0 ? 'font-semibold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}>
                  <td className="py-2">{i === 0 ? 'Esta semana' : `De ${dataCurta(s.semana)}`}</td>
                  <td className="py-2 text-right tabular-nums">{s.visitas}</td>
                  <td className="py-2 text-right tabular-nums">{s.comMedidor}</td>
                  <td className="py-2 text-right tabular-nums">{s.avancaram}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {semanasVazias && (
            <p className="text-sm text-amber-700 dark:text-amber-400 mt-3">
              Nenhuma visita nas últimas 4 semanas.{ultimaVisita ? ` A última foi em ${dataCurta(ultimaVisita)}.` : ''}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-3">
            Semana de segunda a domingo. A mesma empresa visitada duas vezes na semana conta uma vez. "Viraram negociação" olha a situação de hoje da negociação.
          </p>
        </PainelDetalhe>
      )}

      <ListaAtalhos itens={[
        { rotulo: 'Minhas rotas', para: '/crm/rotas', icone: Route },
      ]} />
    </div>
  );
};

export default TelaCampo;
